import { useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLatest } from 'react-use';
import { get } from 'lodash';
import { Status } from 'common/utils/constants';
import { setStatusNotification, updateStatusNotification, deleteStatusNotification } from 'redux/actions';
import { useGainSettings } from 'components/features/GainSettings';

const STATUS_NOTIFICATION_ID = 'gainSettings';
const STATUS_NOTIFICATION_DELAY = 3_000;
const MutationNames = {
  AGC_GAIN_CAPS: 'agcGainCaps',
  OSC_MIT_GAIN: 'oscMitGain',
  AGC_DURATION: 'agcDuration',
  DEFAULT_CABLE_LOSS: 'defaultCableLoss'
};
const statusMessages = {
  [MutationNames.AGC_GAIN_CAPS]: {
    [Status.NONE]: 'Waiting to set AGC Gain Caps',
    [Status.PENDING]: 'Setting AGC Gain Caps',
    [Status.SUCCESS]: 'AGC Gain Caps set successfully',
    [Status.FAILURE]: 'Failed to set AGC Gain Caps'
  },
  [MutationNames.OSC_MIT_GAIN]: {
    [Status.NONE]: 'Waiting to set OSC Mit Gains',
    [Status.PENDING]: 'Setting OSC Mit Gains',
    [Status.SUCCESS]: 'OSC Mit Gains set successfully',
    [Status.FAILURE]: 'Failed to set OSC Mit Gains'
  },
  [MutationNames.AGC_DURATION]: {
    [Status.NONE]: 'Waiting to set DU RX AGC Duration',
    [Status.PENDING]: 'Setting DU RX AGC Duration',
    [Status.SUCCESS]: 'DU RX AGC Duration set successfully',
    [Status.FAILURE]: 'Failed to set DU RX AGC Duration'
  },
  [MutationNames.DEFAULT_CABLE_LOSS]: {
    [Status.NONE]: 'Waiting to set Default Cable Loss',
    [Status.PENDING]: 'Setting Default Cable Loss',
    [Status.SUCCESS]: 'Default Cable Loss set successfully',
    [Status.FAILURE]: 'Failed to set Default Cable Loss'
  }
};

function useDeviceGainSettings(deviceId) {
  const dispatch = useDispatch();
  const device = useSelector((state) => state.ui.pivotOverviewPage.selectedDevices[deviceId]);
  const timeoutRef = useRef(null);
  const deviceRef = useLatest(device);
  const cancelTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);
  const handleMutate = useCallback(
    (mutations) => {
      const actions = mutations.reduce((acc, [variables]) => {
        const networkElement = getNetworkElement(variables);
        const mutationName = getMutationName(variables);

        if (mutationName) {
          const notification = createStatusNotification(deviceRef.current, networkElement, mutationName);
          acc[notification.id] = notification;
        }

        return acc;
      }, {});
      dispatch(
        setStatusNotification({
          id: STATUS_NOTIFICATION_ID,
          mode: 'multi',
          actions
        })
      );
    },
    [dispatch, deviceRef]
  );
  const updateNotification = useCallback(
    (networkElement, mutationName, status) => {
      const notification = getStatusNotification(networkElement, mutationName, status);
      dispatch(
        updateStatusNotification({
          id: STATUS_NOTIFICATION_ID,
          actions: { [notification.id]: notification }
        })
      );
    },
    [dispatch]
  );
  const handleComplete = useCallback(() => {
    cancelTimeout();
    timeoutRef.current = setTimeout(() => {
      dispatch(deleteStatusNotification({ id: STATUS_NOTIFICATION_ID }));
      timeoutRef.current = null;
    }, STATUS_NOTIFICATION_DELAY);
  }, [dispatch, cancelTimeout]);
  const handleAgcGainCapsMutate = useCallback(
    (variables) => updateNotification(getNetworkElement(variables), MutationNames.AGC_GAIN_CAPS, Status.PENDING),
    [updateNotification]
  );
  const handleAgcGainCapsSuccess = useCallback(
    (_, variables) => updateNotification(getNetworkElement(variables), MutationNames.AGC_GAIN_CAPS, Status.SUCCESS),
    [updateNotification]
  );
  const handleAgcGainCapsError = useCallback(
    (_, variables) => updateNotification(getNetworkElement(variables), MutationNames.AGC_GAIN_CAPS, Status.FAILURE),
    [updateNotification]
  );
  const handleOscMitGainMutate = useCallback(
    (variables) => updateNotification(getNetworkElement(variables), MutationNames.OSC_MIT_GAIN, Status.PENDING),
    [updateNotification]
  );
  const handleOscMitGainSuccess = useCallback(
    (_, variables) => updateNotification(getNetworkElement(variables), MutationNames.OSC_MIT_GAIN, Status.SUCCESS),
    [updateNotification]
  );
  const handleOscMitGainError = useCallback(
    (_, variables) => updateNotification(getNetworkElement(variables), MutationNames.OSC_MIT_GAIN, Status.FAILURE),
    [updateNotification]
  );
  const handleAgcDurationMutate = useCallback(
    (variables) => updateNotification(getNetworkElement(variables), MutationNames.AGC_DURATION, Status.PENDING),
    [updateNotification]
  );
  const handleAgcDurationSuccess = useCallback(
    (_, variables) => updateNotification(getNetworkElement(variables), MutationNames.AGC_DURATION, Status.SUCCESS),
    [updateNotification]
  );
  const handleAgcDurationError = useCallback(
    (_, variables) => updateNotification(getNetworkElement(variables), MutationNames.AGC_DURATION, Status.FAILURE),
    [updateNotification]
  );
  const handleDefaultCableLossMutate = useCallback(
    (variables) => updateNotification(getNetworkElement(variables), MutationNames.DEFAULT_CABLE_LOSS, Status.PENDING),
    [updateNotification]
  );
  const handleDefaultCableLossSuccess = useCallback(
    (_, variables) => updateNotification(getNetworkElement(variables), MutationNames.DEFAULT_CABLE_LOSS, Status.SUCCESS),
    [updateNotification]
  );
  const handleDefaultCableLossError = useCallback(
    (_, variables) => updateNotification(getNetworkElement(variables), MutationNames.DEFAULT_CABLE_LOSS, Status.FAILURE),
    [updateNotification]
  );
  useEffect(() => cancelTimeout, [cancelTimeout]);

  return useGainSettings(device, {
    onMutate: handleMutate,
    onComplete: handleComplete,
    onSetAgcGainCapsMutate: handleAgcGainCapsMutate,
    onSetAgcGainCapsSuccess: handleAgcGainCapsSuccess,
    onSetAgcGainCapsError: handleAgcGainCapsError,
    onSetOscMitGainMutate: handleOscMitGainMutate,
    onSetOscMitGainSuccess: handleOscMitGainSuccess,
    onSetOscMitGainError: handleOscMitGainError,
    onSetAgcDurationMutate: handleAgcDurationMutate,
    onSetAgcDurationSuccess: handleAgcDurationSuccess,
    onSetAgcDurationError: handleAgcDurationError,
    onSetDefaultCableLossMutate: handleDefaultCableLossMutate,
    onSetDefaultCableLossSuccess: handleDefaultCableLossSuccess,
    onSetDefaultCableLossError: handleDefaultCableLossError
  });
}

export default useDeviceGainSettings;

function getNetworkElement(variables) {
  if (variables.defaultCableLoss) {
    // For cable loss, use the passed networkElement (SU1, SU2, etc.)
    return variables.networkElement;
  }
  return variables.networkElement ?? 'DU';
}

function getMutationName(variables) {
  return Object.values(MutationNames).find((mutationName) => variables[mutationName] != null);
}

function createStatusNotification(device, networkElement, mutationName) {
  return {
    ...getStatusNotification(networkElement, mutationName, Status.NONE),
    type: networkElement === 'DU' ? 'donor' : 'service',
    deviceId:
      // maybe better to use {networkElement}.inventory-info.serial-id
      networkElement === 'DU'
        ? device?.deviceId
        : get(device, ['properties', 'reported', networkElement, 'provision-info', 'expected-serial-id'])
  };
}

function getStatusNotification(networkElement, mutationName, status) {
  return {
    id: [networkElement, mutationName].join('/'),
    status,
    text: statusMessages[mutationName][status]
  };
}
